/**
 * VuePrint v1.6.3
 * Copyright (c) 2024 Jak Guru LLC
 * Vueprint a commerical work product released under the MIT License
 * and is provided as-is with no warranty or guarantee of support.
 * Documentation: https://jakguru.github.io/vueprint/
 * 
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

import Swal from 'sweetalert2';
import { m as merge } from './vueprint.22af4dba.mjs';
import * as Tone from 'tone';
import { Notyf } from 'notyf';
import { g as getDebugger } from './vueprint.b84f56b4.mjs';

var __accessCheck = (obj, member, msg) => {
  if (!member.has(obj))
    throw TypeError("Cannot " + msg);
};
var __privateGet = (obj, member, getter) => {
  __accessCheck(obj, member, "read from private field");
  return getter ? getter.call(obj) : member.get(obj);
};
var __privateAdd = (obj, member, value) => {
  if (member.has(obj))
    throw TypeError("Cannot add the same private member more than once");
  member instanceof WeakSet ? member.add(obj) : member.set(obj, value);
};
var _sounds;
const defaults = {
  backdrop: true,
  color: "#000000",
  customClass: {
    title: "text-h6",
    htmlContainer: "text-body-2",
    popup: "v-card v-card--density-default v-card--variant-elevated bg-surface elevation-3 pb-3",
    confirmButton: "v-btn v-btn--elevated bg-primary v-btn--density-default v-btn--size-default v-btn--variant-elevated",
    denyButton: "v-btn v-btn--elevated bg-error v-btn--density-default v-btn--size-default v-btn--variant-elevated",
    cancelButton: "v-btn v-btn--elevated bg-cancel v-btn--density-default v-btn--size-default v-btn--variant-elevated"
  },
  buttonsStyling: true,
  reverseButtons: true
};
const swal = Swal.mixin(defaults);
const toastDefaults = merge({}, defaults, {
  toast: true,
  position: "top-end",
  showConfirmButton: false,
  timer: 3e3,
  timerProgressBar: true,
  backdrop: void 0,
  didOpen: (toast2) => {
    toast2.onmouseenter = Swal.stopTimer;
    toast2.onmouseleave = Swal.resumeTimer;
  }
});
delete toastDefaults.backdrop;
const toast = Swal.mixin(toastDefaults);
const sdbg = getDebugger("Sounds");
class SoundsService {
  /**
   * Creates a new SoundsService
   * @param sounds A map of sound names to their urls
   */
  constructor(sounds) {
    __privateAdd(this, _sounds, {});
    if (sounds) {
      sdbg("Initializing sounds", sounds);
      this.add(sounds);
    }
  }
  /**
   * Lists the available sound keys
   * @returns A list of the available sound keys
   */
  list() {
    return Object.keys(__privateGet(this, _sounds));
  }
  /**
   * Returns all the Tone.Player objects for all sounds
   */
  all() {
    const ret = { ...__privateGet(this, _sounds) };
    Object.freeze(ret);
    return ret;
  }
  /**
   * Adds sounds to the service
   * @param sounds A map of sound names to their urls
   */
  add(sounds) {
    Object.keys(sounds).forEach((key) => {
      if (!__privateGet(this, _sounds)[key]) {
        sdbg("Adding sound", key);
        const sound = sounds[key];
        __privateGet(this, _sounds)[key] = new Tone.Player({
          url: sound
        }).toDestination();
      }
    });
  }
  /**
   * Gets a sound from the service
   * @param key The key of the sound to retrieve
   * @returns The Tone.Player object for the sound
   */
  get(key) {
    return __privateGet(this, _sounds)[key];
  }
  /**
   * Plays a sound
   * @param key The key of the sound to play
   */
  play(key) {
    const sound = __privateGet(this, _sounds)[key];
    if (sound) {
      sdbg("Playing sound", key);
      sound.start();
    }
  }
  /**
   * Stops a sound
   * @param key The key of the sound to stop
   */
  stop(key) {
    const sound = __privateGet(this, _sounds)[key];
    if (sound) {
      sdbg("Stopping sound", key);
      sound.stop();
    }
  }
}
_sounds = new WeakMap();
const notyf = new Notyf({
  dismissible: true
});

const ui = {
  __proto__: null,
  SoundsService: SoundsService,
  notyf: notyf,
  swal: swal,
  toast: toast
};

export { SoundsService as S, notyf as n, swal as s, toast as t, ui as u };
//# sourceMappingURL=vueprint.c0789fba.mjs.map
